import { useMemo } from 'react';

import { monorepoRoutePaths, routePaths as adminRoutePaths, routePaths } from '@apps/admin/route.paths';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { EventPageSlugs } from '@apps/admin/routes/Dashboard/common/enums/EventPageSlugs';
import { useRouterHelper, useTranslation } from '@shared/core';
import {
  Add,
  Calendar,
  Design,
  EditPages,
  Faq,
  Guests,
  Home,
  Photos,
  Registry,
  Settings,
  Story,
  Plane,
  Video,
  Bed,
  CustomPage,
  WebGlobe,
  Experiences,
} from '@withjoy/joykit/icons';

import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { ProductRowSubItem } from '../../PrimaryDashboardContent/ProductRowItem';
import type { PageInfo } from './useProductRows';
import { useGetWebsiteDesignerVisitStatusQuery } from '../../../../Dashboard.graphql.generated';
import { EventDesignPurpose, EventPageVisibility, PageKind } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventFeatureFlagsContext } from '@shared/core/featureFlags/eventFeatureFlags';
import { useThingsToDoPage } from '@website-editor/utils/ThingsToDo.util';
import { filterMigratedLegacyPages, shouldUseNativePageEntry, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useAddCustomPageDialogContext } from '@shared/context/AddCustomPageDialog/AddCustomPageDialog.provider';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';

export const useWebsiteProductRow = (
  eventPages: ReadonlyArray<PageInfo>,
  productVerticals: ProductVerticals,
  pageContainers: ReadonlyArray<{ id: string; visibility: PageInfo['visibility'] }> = [],
): ProductRow => {
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { tq } = useTranslation('adminDashboardV2');
  const { openAddCustomPageDialog, setCustomPageDialogData } = useAddCustomPageDialogContext();
  const useCarbonScopedWebsiteEditor = useProFeature(EventFeature.useCarbonScopedWebsiteEditor);
  const { isMigratedCustomPageEnabled, isLoading: isMigratedFlagsLoading } = useMigratedCustomPageFeatureFlags();

  const { isEventFeatureFlagEnabled } = useEventFeatureFlagsContext();
  const isPerGuestVisibilityEnabled = isEventFeatureFlagEnabled('websitepageperguestvisibility');

  const { eventHandle } = useEventInfo();

  // Hide the Video tile while feature flags are still resolving — otherwise events
  // that should be sunset would briefly expose a link to /edit/video.
  const [isSunsetVideoPageOn, isSunsetVideoPageLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'sunsetVideoPageEnabled' });
  const isVideoPageSunset = isSunsetVideoPageLoading || isSunsetVideoPageOn;

  const { data: websiteDesignerVisitStatus, loading: isLoadingVisitStatus } = useGetWebsiteDesignerVisitStatusQuery({
    variables: { eventHandle: eventHandle || '', purpose: EventDesignPurpose.live },
    batchMode: 'fast',
    skip: !eventHandle,
  });

  const { isThingsToDoSlug } = useThingsToDoPage(eventPages);

  const websiteProductRow = useMemo<ProductRow>(() => {
    const isEnabled = !productVerticals.optOutOfWebsite;
    // `eventPages` returns both the legacy native page and its migrated custom replacement
    // during the migration window — filter the legacy duplicate up front so every downstream
    // map sees a single canonical row per page (otherwise iteration order decides whose
    // title/visibility wins).
    const filteredPages = filterMigratedLegacyPages(eventPages, { isMigratedCustomPageEnabled, isFeatureFlagsLoading: isMigratedFlagsLoading });
    const pageVisibilityMap = new Map<string, PageInfo['visibility']>();
    filteredPages.forEach(page => {
      // Migrated custom pages render via native dictionary entries keyed by pageSlug (see the
      // editItemsDictionary below), so key their visibility by pageSlug too — otherwise the
      // burger menu's Edit Pages dropdown reads an undefined visibility for them.
      // Canonicalize the native key so migrated registrylinks (pageSlug='registry') is reached
      // via EventPageSlugs.Registry ('registrylinks') downstream.
      const useNativeKey = shouldUseNativePageEntry(page, { isMigratedCustomPageEnabled, isFeatureFlagsLoading: isMigratedFlagsLoading });
      const key = useNativeKey ? (toCanonicalMigratedSlug(page.pageSlug) ?? page.pageSlug) : page.id;
      pageVisibilityMap.set(key, page.visibility);
    });

    // Real container visibility keyed the same way as pageVisibilityMap (join by id, matching
    // usePagesWithPhotoAlignment). Only populated when the per-guest flag is on, so downstream a
    // present specificGuests value means "render as specific guests, not hidden".
    const containerVisibilityMap = new Map<string, PageInfo['visibility']>();
    if (isPerGuestVisibilityEnabled) {
      const containerVisibilityById = new Map(pageContainers.map(container => [container.id, container.visibility]));
      filteredPages.forEach(page => {
        const containerVisibility = containerVisibilityById.get(page.id);
        if (!containerVisibility) return;
        const useNativeKey = shouldUseNativePageEntry(page, { isMigratedCustomPageEnabled, isFeatureFlagsLoading: isMigratedFlagsLoading });
        const key = useNativeKey ? (toCanonicalMigratedSlug(page.pageSlug) ?? page.pageSlug) : page.id;
        containerVisibilityMap.set(key, containerVisibility);
      });
    }
    const pageSlugToTitleMap = filteredPages.reduce<Record<string, string>>((acc, page) => {
      if (page.pageSlug && page.pageTitle) {
        // Canonicalize so migrated registrylinks pages are keyed under 'registrylinks' — matches
        // the EventPageSlugs lookups used by the dictionary entries.
        const key = toCanonicalMigratedSlug(page.pageSlug) ?? page.pageSlug;
        acc[key] = page.pageTitle;
      }
      return acc;
    }, {});

    let editPagesSubItems: ProductRowSubItem[] = [];
    const editItemsDictionary = {
      [EventPageSlugs.Welcome]: [
        {
          title: tq`Welcome`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Welcome],
          icon: Home,
          path: routeHelpers.buildPath(monorepoRoutePaths.welcome),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'welcome', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Welcome) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Welcome),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Welcome),
          telemetryName: monorepoRoutePaths.welcome,
        },
        ...(isVideoPageSunset
          ? []
          : [
              {
                title: tq`Video`,
                icon: Video,
                path: routeHelpers.buildPath(monorepoRoutePaths.video),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'video', section: fromDropdown ? 'Website' : '', categoryArg }),
                isDisabled: pageVisibilityMap.get(EventPageSlugs.Video) === EventPageVisibility.hidden,
                pageVisibility: pageVisibilityMap.get(EventPageSlugs.Video),
                containerVisibility: containerVisibilityMap.get(EventPageSlugs.Video),
                telemetryName: monorepoRoutePaths.video,
              },
            ]),
      ],
      [EventPageSlugs.Story]: [
        {
          title: tq`Story & Tidbits`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Story],
          icon: Story,
          path: routeHelpers.buildPath(monorepoRoutePaths.story),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'story', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Story) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Story),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Story),
          telemetryName: monorepoRoutePaths.story,
        },
      ],
      [EventPageSlugs.Schedule]: [
        {
          title: tq`Schedule`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Schedule],
          icon: Calendar,
          path: routeHelpers.buildPath(monorepoRoutePaths.schedule),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'schedule', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Schedule) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Schedule),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Schedule),
          telemetryName: monorepoRoutePaths.schedule,
        },
      ],
      [EventPageSlugs.Travel]: [
        {
          title: tq`Travel`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Travel],
          icon: Plane,
          path: routeHelpers.buildPath(monorepoRoutePaths.travel),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'travel', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Travel) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Travel),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Travel),
          telemetryName: monorepoRoutePaths.travel,
        },
      ],
      [EventPageSlugs.Accommodations]: [
        {
          title: tq`Where to Stay`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Accommodations],
          icon: Bed,
          path: routeHelpers.buildPath(EventPageSlugs.Accommodations),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'adminAccommodations', section: fromDropdown ? 'Website' : '', categoryArg }),
          hasNewPill: true,
          pathIsInJoyWeb: true,
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Accommodations) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Accommodations),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Accommodations),
          telemetryName: EventPageSlugs.Accommodations,
        },
      ],
      [EventPageSlugs.Faq]: [
        {
          title: tq`Guest Q&A`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Faq],
          icon: Faq,
          path: routeHelpers.buildPath(monorepoRoutePaths.faq),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'faq', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Faq) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Faq),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Faq),
          telemetryName: monorepoRoutePaths.faq,
        },
      ],
      [EventPageSlugs.WeddingParty]: [
        {
          title: tq`Wedding Party`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.WeddingParty],
          icon: Guests,
          path: routeHelpers.buildPath(monorepoRoutePaths.vip),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'vip', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.WeddingParty) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.WeddingParty),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.WeddingParty),
          telemetryName: monorepoRoutePaths.vip,
        },
      ],
      [EventPageSlugs.Vip]: [
        {
          title: tq`Wedding Party`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.WeddingParty],
          icon: Guests,
          path: routeHelpers.buildPath(monorepoRoutePaths.vip),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'vip', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Vip) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Vip),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Vip),
          telemetryName: monorepoRoutePaths.vip,
        },
      ],
      [EventPageSlugs.Registry]: [
        {
          title: tq`Registry`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Registry],
          icon: Registry,
          path: routeHelpers.buildPath(EventPageSlugs.Registry),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'registrylinks', section: fromDropdown ? 'Website' : '', categoryArg }),
          isDisabled: pageVisibilityMap.get(EventPageSlugs.Registry) === EventPageVisibility.hidden,
          pageVisibility: pageVisibilityMap.get(EventPageSlugs.Registry),
          containerVisibility: containerVisibilityMap.get(EventPageSlugs.Registry),
          telemetryName: EventPageSlugs.Registry,
        },
      ],
      [EventPageSlugs.Custom]: (id: string, pageSlug: string, pageTitle?: string) => {
        return [
          {
            id: `website-custom-${pageSlug}`,
            title: pageTitle ?? '',
            customTitle: pageTitle ?? '',
            icon: isThingsToDoSlug(pageSlug) ? Experiences : CustomPage,
            hasNewPill: false,
            path: routeHelpers.buildPath(routePaths.customPage.goToPath(pageSlug)),
            onClick: (categoryArg?: string, fromDropdown?: boolean) =>
              telemetry.openPage({ label: `website.custompage`, section: fromDropdown ? 'Website' : '', categoryArg, pageSlug }),
            pathIsInJoyWeb: true,
            isDisabled: pageVisibilityMap.get(id) === EventPageVisibility.hidden,
            pageVisibility: pageVisibilityMap.get(id),
            containerVisibility: containerVisibilityMap.get(id),
            telemetryName: pageSlug,
          },
        ];
      },
    };

    // `filteredPages` already drops the legacy duplicate, so the dashboard dropdown shows one
    // entry per page during the migration window.
    filteredPages.forEach(page => {
      // Canonicalize so migrated registrylinks pages (pageSlug='registry') hit the 'registrylinks'
      // dictionary entry instead of falling through to generic custom-page handling.
      const lookupSlug = toCanonicalMigratedSlug(page.pageSlug) ?? page.pageSlug;
      const item = editItemsDictionary[lookupSlug as keyof typeof editItemsDictionary];

      if (item && typeof item !== 'function' && shouldUseNativePageEntry(page, { isMigratedCustomPageEnabled, isFeatureFlagsLoading: isMigratedFlagsLoading })) {
        editPagesSubItems = editPagesSubItems.concat(item);
      } else if (page.pageKind !== PageKind.systemLegacy) {
        const itemCustom = editItemsDictionary[EventPageSlugs.Custom as keyof typeof editItemsDictionary];
        if (typeof itemCustom === 'function') {
          editPagesSubItems = editPagesSubItems.concat(itemCustom(page.id, page.pageSlug, page?.pageTitle));
        }
      }
    });

    const accommodationTileIndex = editPagesSubItems.findIndex(item => item.telemetryName === EventPageSlugs.Accommodations);

    return {
      title: tq`Website`,
      isEnabled,
      children: [
        {
          id: 'website-overview',
          title: tq`Overview`,
          icon: WebGlobe,
          path:
            !isLoadingVisitStatus && !websiteDesignerVisitStatus?.eventByName?.eventDesign?.hasVisitedDesignPage
              ? routeHelpers.buildPath(adminRoutePaths.design.path)
              : routeHelpers.buildPath(adminRoutePaths.websiteOverview.path),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'websiteOverview', section: fromDropdown ? 'Website' : '', categoryArg }),
          pathIsInJoyWeb: true,
          telemetryName: adminRoutePaths.websiteOverview.path,
        },
        {
          id: 'website-design',
          title: tq`Design`,
          icon: Design,
          path: routeHelpers.buildPath(adminRoutePaths.design.path),
          pathIsInJoyWeb: true,
          telemetryName: adminRoutePaths.design.path,
          onClick: () => telemetry.designTabClicked(),
          hideDropdownLabel: true,
        },
        {
          id: 'website-photos',
          title: tq`Photos`,
          icon: Photos,
          path: routeHelpers.buildPath(adminRoutePaths.photoCollection.path),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'photos', section: fromDropdown ? 'Website' : '', categoryArg }),
          telemetryName: adminRoutePaths.photoCollection.path,
        },
        ...(accommodationTileIndex !== -1 ? [{ id: 'website-accommodations', ...editPagesSubItems[accommodationTileIndex], hasNewPill: false }] : []),
        {
          id: 'website-editPages',
          title: tq`Edit Pages`,
          icon: EditPages,
          tileBehavior: 'openDropdown',
          hideDropdownLabel: true,
          subItems: [
            { title: tq`Pages`, isSubhead: true },
            ...editPagesSubItems,
            {
              title: tq`Add New Page`,
              icon: Add,
              onClick: (_categoryArg?: string) => {
                telemetry.addNewPageOpen();
                openAddCustomPageDialog();
                setCustomPageDialogData({ pages: [...eventPages] });
              },
              hasDivider: true,
              telemetryName: 'addNewPage',
            },

            {
              title: tq`Manage Pages`,
              icon: Settings,
              path: useCarbonScopedWebsiteEditor ? routeHelpers.buildPath(adminRoutePaths.websiteOverview.path) : routeHelpers.buildPath(monorepoRoutePaths.settingsMenu),
              pathIsInJoyWeb: useCarbonScopedWebsiteEditor ? true : undefined,
              onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'managePages', section: fromDropdown ? 'Website' : '', categoryArg }),
              hasDivider: false,
              telemetryName: useCarbonScopedWebsiteEditor ? adminRoutePaths.websiteOverview.path : monorepoRoutePaths.settingsMenu,
            },
          ],
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventPages,
    routeHelpers,
    telemetry,
    productVerticals,
    tq,
    websiteDesignerVisitStatus?.eventByName?.eventDesign?.hasVisitedDesignPage,
    isLoadingVisitStatus,
    useCarbonScopedWebsiteEditor,
    isVideoPageSunset,
    isMigratedCustomPageEnabled,
    isMigratedFlagsLoading,
    pageContainers,
    isPerGuestVisibilityEnabled,
  ]);

  return websiteProductRow;
};
